/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import mitt, { type Emitter } from 'mitt'

type Events = {
  FILTER_TOGGLE: void
  FILTER_UPDATE: void
  TABLE_UPDATE: void
  UPDATE_SEARCH_BAR_FILTER: void
  updatePolicies: void
  updateDateFormat: void
  updateCache_users: void
}

// Create singleton event bus using mitt
export const eventBus: Emitter<Events> = mitt<Events>()

/**
 * Event creator factory for ts-bus compatibility
 * Usage with default export: Events.FILTER_TOGGLE({ collapsed: true })
 * This maintains backward compatibility with legacy components using ts-bus pattern
 */
const createEventDefinition =
  <T = any>() =>
  (type: string) =>
  (payload?: T) => ({
    type,
    payload
  })

/**
 * Event creator functions (default export)
 * Maintains backward compatibility with ts-bus pattern from legacy code
 * @example
 * const event = Events.FILTER_TOGGLE({ collapsed: true })
 * bus.dispatchEvent(new CustomEvent(event.type, { detail: event.payload }))
 */
export default {
  FILTER_TOGGLE: createEventDefinition<{ collapsed: boolean }>()('FILTER_TOGGLE'),
  FILTER_UPDATE: createEventDefinition<{ filter: any }>()('FILTER_UPDATE'),
  TABLE_UPDATE: createEventDefinition()('TABLE_UPDATE'),
  UPDATE_SEARCH_BAR_FILTER: createEventDefinition()('UPDATE_SEARCH_BAR_FILTER')
}

/**
 * String constants for direct eventBus usage (named export)
 * Use with mitt eventBus for type-safe event emission and listening
 * @example
 * eventBus.emit('FILTER_UPDATE', filterData)
 * eventBus.on('FILTER_UPDATE', handler)
 */
export const EventTypes = {
  FILTER_TOGGLE: 'FILTER_TOGGLE',
  FILTER_UPDATE: 'FILTER_UPDATE',
  TABLE_UPDATE: 'TABLE_UPDATE',
  UPDATE_SEARCH_BAR_FILTER: 'UPDATE_SEARCH_BAR_FILTER',
  updatePolicies: 'updatePolicies',
  updateDateFormat: 'updateDateFormat',
  updateCache_users: 'updateCache_users'
} as const
